<template>
  <div data-component="base-color-picker">
    <or-tooltip :content="$t('HITL.common.labels.reset-default-color')">
      <or-icon-button
        data-button="base-color-picker-reset"
        :disabled="isLoading || isDisabled"
        icon="replay"
        @click="$emit('reset')"
      />
    </or-tooltip>

    <or-input
      data-input="base-color-picker-text-input"
      size="s"
      :model-value="color"
      :placeholder="COLOR_PLACEHOLDER"
      :disabled="isLoading || isDisabled"
      @input="onTextInput"
    />

    <input
      :value="color"
      :disabled="isLoading || isDisabled"
      type="color"
      @change="$emit('input', $event.target.value)"
    >
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import {
  OrIconButtonV3 as OrIconButton,
  OrInputV3 as OrInput,
  OrTooltipV3 as OrTooltip,
} from '@onereach/ui-components-25';

export default defineComponent({
  name: 'BaseColorPicker',
  components: {
    OrIconButton,
    OrInput,
    OrTooltip,
  },
  props: {
    color: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    isLoading: Boolean,
    isDisabled: Boolean,
  },
  emits: ['input', 'reset'],
  setup() {
    return {
      COLOR_PLACEHOLDER: '#000000',
      COLOR_REG_EXP: /^#?[0-9a-fA-F]{0,6}$/g,
    };
  },
  methods: {
    onTextInput(event: KeyboardEvent) {
      const target = event.target as HTMLInputElement;
      if (target.value.match(this.COLOR_REG_EXP)) {
        const color = target.value.startsWith('#')
          ? target.value
          : target.value
            ? `#${target.value}`
            : '';
        this.$emit('input', color);
      } else {
        target.value = target.value.slice(0, -1);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='base-color-picker'] {
  --base-color-picker-border-width: var(--s-1);
  --base-color-picker-size: calc(var(--s-5) + var(--base-color-picker-border-width));

  display: flex;
  align-items: center;
  gap: var(--s-4);

  [data-input='base-color-picker-text-input'] {
    width: 6.25rem;
  }

  input[type='color'] {
    appearance: none;
    -moz-appearance: none;
    -webkit-appearance: none;
    background: none;
    height: var(--base-color-picker-size);
    width: var(--base-color-picker-size);
    padding: 0;
    border: var(--base-color-picker-border-width) solid var(--or-c-on-surface-variant);
    border-radius: 50%;
    cursor: pointer;

    /* TODO: -webkit and -moz selectors can't be merged? */
    &::-webkit-color-swatch {
      border: 0;
      border-radius: 50%;
    }

    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }

    &::-moz-color-swatch,
    &::-moz-focus-inner {
      border: 0;
      border-radius: 50%;
    }

    &::-moz-focus-inner {
      padding: 0;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
}
</style>
